import { useMemo } from "react";
import { useFetch } from "./useFetch";
import { apiEndpoints, API_BASE_URL } from "../utils/api";

// Hook base para datos del sitio
export const useSEOData = () => {
  const { data: settings } = useFetch(apiEndpoints.settings.public);

  return {
    siteName: settings?.site?.name || "Red Arauca",
    siteDescription: settings?.site?.description || "Red de medios de comunicación de Arauca - Tu fuente confiable de información regional",
    siteLogo: settings?.site?.logo || "/assets/logo.png",
    siteUrl: settings?.site?.url || window.location.origin,
    defaultImage: settings?.site?.og_image || "/assets/default-og-image.jpg",
    twitterHandle: settings?.social?.twitter?.replace("https://twitter.com/", "@") || "@redarauca",
    facebookPage: settings?.social?.facebook || null,
    settings // Mantener referencia a settings completo
  };
};

// Hook principal para datos SEO de posts
export const usePostSEO = (post) => {
  const seoData = useSEOData();
  
  return useMemo(() => {
    if (!post) return null;

    // Construir URL de imagen con múltiples fallbacks
    const getImageUrl = () => {
      const images = [
        post.featured_image,
        post.image,
        post.thumbnail,
        post.cover_image
      ].filter(Boolean);

      if (images.length === 0) {
        return `${window.location.origin}/assets/default-og-image.jpg`;
      }

      const imageUrl = images[0];
      
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }

      if (imageUrl.startsWith('/uploads/')) {
        return `${API_BASE_URL}${imageUrl}`;
      }

      if (imageUrl.startsWith('/')) {
        return `${window.location.origin}${imageUrl}`;
      }

      return `${API_BASE_URL}/uploads/${imageUrl}`;
    };

    // Construir descripción con fallbacks
    const getDescription = () => {
      const descriptions = [
        post.excerpt,
        post.description,
        post.summary,
        post.meta_description
      ].filter(Boolean);

      if (descriptions.length === 0) {
        // Intentar extraer del body/content
        const content = post.body || post.content || '';
        const plainText = content.replace(/<[^>]*>/g, '').trim();
        if (plainText) {
          return plainText.substring(0, 160) + (plainText.length > 160 ? '...' : '');
        }
        return `Lee la última noticia: "${post.title}" en Red Arauca, tu fuente confiable de información.`;
      }

      return descriptions[0].length > 160 
        ? descriptions[0].substring(0, 160) + '...'
        : descriptions[0];
    };

    // Procesar tags/keywords
    const getKeywords = () => {
      const tags = [];
      
      // Tags del post
      if (post.tags) {
        if (typeof post.tags === 'string') {
          tags.push(...post.tags.split(',').map(tag => tag.trim()));
        } else if (Array.isArray(post.tags)) {
          tags.push(...post.tags);
        }
      }

      // Categoría
      if (post.category || post.category_name) {
        tags.push(post.category || post.category_name);
      }

      // Keywords base
      tags.push('noticias', 'Arauca', 'Colombia', 'Red Arauca');

      return [...new Set(tags)].join(', ');
    };

    // Calcular tiempo de lectura
    const getReadingTime = () => {
      if (post.reading_time) return post.reading_time;
      
      const content = post.body || post.content || '';
      const plainText = content.replace(/<[^>]*>/g, '');
      const wordCount = plainText.split(/\s+/).length;
      return Math.max(1, Math.ceil(wordCount / 200));
    };

    // Fecha de publicación en formato ISO
    const getPublishedTime = () => {
      const dateString = post.published_at || post.created_at || post.date;
      if (!dateString) return new Date().toISOString();
      
      try {
        return new Date(dateString).toISOString();
      } catch (error) {
        return new Date().toISOString();
      }
    };

    // Fecha de modificación
    const getModifiedTime = () => {
      const dateString = post.updated_at || post.modified_at;
      if (!dateString) return getPublishedTime();
      
      try {
        return new Date(dateString).toISOString();
      } catch (error) {
        return getPublishedTime();
      }
    };

    // URL del post
    const getPostUrl = () => {
      const baseUrl = window.location.origin;
      return `${baseUrl}/post/${post.slug}`;
    };

    return {
      title: post.title,
      description: getDescription(),
      keywords: getKeywords(),
      image: getImageUrl(),
      url: getPostUrl(),
      type: "article",
      author: post.author || post.author_name || post.username,
      article: {
        publishedTime: getPublishedTime(),
        modifiedTime: getModifiedTime(),
        author: post.author || post.author_name || post.username || "Red Arauca",
        section: post.category || post.category_name || "Noticias",
        tags: getKeywords().split(', '),
        readingTime: getReadingTime(),
        wordCount: (post.body || post.content || '').replace(/<[^>]*>/g, '').split(/\s+/).length
      }
    };
  }, [post, seoData]);
};

// Hook para SEO de categorías
export const useCategorySEO = (category) => {
  const seoData = useSEOData();
  
  return useMemo(() => {
    if (!category) return null;

    const baseUrl = window.location.origin;
    const categoryName = category.name || 'Categoría';
    const categorySlug = category.slug || 'categoria';

    const getImageUrl = (imagePath) => {
      if (!imagePath) return seoData.defaultImage;
      
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
      }
      
      return `${API_BASE_URL}/uploads/${imagePath}`;
    };

    return {
      title: `${categoryName} - Noticias y Artículos`,
      description: category.description || `Todas las noticias y artículos de ${categoryName} en Red Arauca. Mantente informado con nuestra cobertura especializada.`,
      keywords: `${categoryName.toLowerCase()}, noticias, artículos, Red Arauca, Colombia, información`,
      image: getImageUrl(category.image),
      url: `${baseUrl}/categoria/${categorySlug}`,
      type: "website"
    };
  }, [category, seoData]);
};

// Hook para SEO de homepage
export const useHomeSEO = () => {
  const seoData = useSEOData();
  
  return useMemo(() => {
    const baseUrl = window.location.origin;
    
    return {
      title: "",
      description: seoData.siteDescription,
      keywords: "Red Arauca, noticias Arauca, noticias Colombia, medios comunicación, periodismo, información regional, actualidad",
      image: `${baseUrl}/assets/home-og-image.jpg`,
      url: baseUrl,
      type: "website"
    };
  }, [seoData]);
};

// Hook para SEO de páginas generales
export const usePageSEO = ({ title, description, keywords, image, path = '' }) => {
  const seoData = useSEOData();
  
  return useMemo(() => {
    const baseUrl = window.location.origin;
    
    return {
      title,
      description: description || `${title} - Red Arauca, tu fuente confiable de información en Arauca y Colombia.`,
      keywords: keywords || `${title.toLowerCase()}, Red Arauca, información, noticias`,
      image: image || `${baseUrl}/assets/page-og-image.jpg`,
      url: `${baseUrl}${path}`,
      type: "website"
    };
  }, [title, description, keywords, image, path, seoData]);
};

// Hook para SEO de búsqueda
export const useSearchSEO = (query, results = 0) => {
  const seoData = useSEOData();
  
  return useMemo(() => {
    const baseUrl = window.location.origin;
    
    if (!query) {
      return {
        title: "Buscar noticias y artículos",
        description: "Busca noticias, artículos y contenido en Red Arauca. Encuentra la información que necesitas de Arauca y Colombia.",
        keywords: "buscar, noticias, artículos, Red Arauca, búsqueda",
        image: `${baseUrl}/assets/search-og-image.jpg`,
        url: `${baseUrl}/buscar`,
        type: "website",
        noindex: true
      };
    }

    const resultsCount = results?.pagination?.total || results || 0;

    return {
      title: `Resultados para "${query}"`,
      description: `${resultsCount} resultado${resultsCount !== 1 ? 's' : ''} encontrado${resultsCount !== 1 ? 's' : ''} para "${query}" en Red Arauca. Explora nuestras noticias y artículos.`,
      keywords: `${query}, búsqueda, noticias, Red Arauca, resultados`,
      image: `${baseUrl}/assets/search-results-og-image.jpg`,
      url: `${baseUrl}/buscar?q=${encodeURIComponent(query)}`,
      type: "website",
      noindex: resultsCount === 0 // No indexar si no hay resultados
    };
  }, [query, results, seoData]);
};

// Hook para SEO de miembros/medios
export const useMembersSEO = (activeFilter = 'all', count = 0) => {
  const seoData = useSEOData();
  
  return useMemo(() => {
    const baseUrl = window.location.origin;
    const filterNames = {
      all: 'Todos nuestros miembros',
      radio: 'Emisoras de radio',
      print: 'Medios impresos',
      digital: 'Medios digitales',
      tv: 'Canales de televisión'
    };
    
    const filterName = filterNames[activeFilter] || 'Miembros';
    
    return {
      title: `${filterName} - Red Arauca`,
      description: `Conoce ${filterName.toLowerCase()} que forman parte de Red Arauca. ${count}+ medios de comunicación comprometidos con el periodismo de calidad en Arauca y Colombia.`,
      keywords: "medios comunicación, Red Arauca, miembros, radio, prensa, digital, televisión, periodismo",
      image: `${baseUrl}/assets/members-og-image.jpg`,
      url: `${baseUrl}/miembros${activeFilter !== 'all' ? `?tipo=${activeFilter}` : ''}`,
      type: "website"
    };
  }, [activeFilter, count, seoData]);
};

// Utilidades adicionales
export const generateSocialShareUrls = (url, title, description) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const encodedText = encodeURIComponent(`${title} ${url}`);

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    whatsapp: `https://wa.me/?text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };
};

// Validador de URLs de imágenes
export const validateImageUrl = async (imageUrl) => {
  if (!imageUrl) return false;
  
  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    return response.ok && response.headers.get('content-type')?.startsWith('image/');
  } catch (error) {
    return false;
  }
};